<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">{{ isSeller ? 'View Payout' : 'Edit Payout' }}</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        {{ isSeller ? 'View payout details and transaction information' : 'Modify payout details and transaction information' }}
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button (hidden for sellers) -->
                    <v-btn
                      v-if="!isSeller"
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                    >
                      <v-icon left>{{ isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                      {{ isEditMode ? 'Read Only' : 'Edit Mode' }}
                    </v-btn>

                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Payouts
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Payout Information</h2>
              <v-spacer></v-spacer>
              <v-chip 
                :color="isEditMode ? 'success' : 'warning'" 
                :outlined="!isEditMode" 
                x-small
              >
                <v-icon left x-small>
                  {{ isEditMode ? 'mdi-pencil' : 'mdi-eye' }}
                </v-icon>
                {{ isEditMode ? 'Edit Mode' : 'Read Only Mode' }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>
            
            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                
                <!-- Payout Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cash</v-icon>
                    Payout Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="users"
                        label="User"
                        placeholder="Select user"
                        solo
                        rounded
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'User is required']"
                        :error-messages="errors.user_id"
                        class="professional-field"
                        hide-details="auto"
                        required
                        :loading="loadingUsers"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                        <template #item="{ item }">
                          <v-list-item-avatar>
                            <v-avatar size="32" color="primary">
                              <span class="white--text text-caption">{{
                                getInitials(item.text)
                              }}</span>
                            </v-avatar>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.amount"
                        label="Payout Amount"
                        placeholder="Enter payout amount"
                        solo
                        rounded
                        type="number"
                        step="0.01"
                        min="0.01"
                        :disabled="!isEditMode || !canModifyAmount"
                        :readonly="!isEditMode || !canModifyAmount"
                        :rules="amountRules"
                        :error-messages="errors.amount"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-currency-inr</v-icon>
                        </template>
                      </v-text-field>
                      <p v-if="!canModifyAmount" class="text-caption warning--text mt-1">
                        Amount cannot be modified for processed payouts
                      </p>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.payment_method"
                        :items="paymentMethods"
                        label="Payment Method"
                        placeholder="Select payment method"
                        solo
                        rounded
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.payment_method"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-credit-card</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.payout_status"
                        :items="payoutStatuses"
                        label="Payout Status"
                        placeholder="Select payout status"
                        solo
                        rounded
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.payout_status"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-cash-check</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Transaction Details Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-receipt</v-icon>
                    Transaction Details
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.generated_transaction_id"
                        label="Generated Transaction ID"
                        solo
                        rounded
                        readonly
                        disabled
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-identifier</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.transaction_id"
                        label="Payment Gateway Transaction ID"
                        placeholder="Enter transaction ID from payment gateway"
                        solo
                        rounded
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.transaction_id"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-credit-card-sync</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-menu
                        v-model="datePickerMenu"
                        :close-on-content-click="false"
                        :disabled="!isEditMode"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="auto"
                      >
                        <template #activator="{ on, attrs }">
                          <v-text-field
                            :value="formattedPaymentDateTime"
                            label="Payment Date & Time"
                            prepend-inner-icon="mdi-calendar-clock"
                            solo
                            rounded
                            readonly
                            :disabled="!isEditMode"
                            v-bind="attrs"
                            v-on="isEditMode ? on : {}"
                            :error-messages="errors.payment_date"
                            class="professional-field"
                            hide-details="auto"
                            clearable
                            @click:clear="clearPaymentDate"
                          />
                        </template>
                        <v-card>
                          <v-card-text class="pa-0">
                            <v-tabs v-model="dateTimeTab" fixed-tabs>
                              <v-tab key="date">
                                <v-icon>mdi-calendar</v-icon>
                                Date
                              </v-tab>
                              <v-tab key="time" :disabled="!form.payment_date">
                                <v-icon>mdi-clock</v-icon>
                                Time
                              </v-tab>
                            </v-tabs>
                            <v-tabs-items v-model="dateTimeTab">
                              <v-tab-item key="date">
                                <v-date-picker
                                  v-model="form.payment_date"
                                  no-title
                                  scrollable
                                  color="primary"
                                  @input="dateTimeTab = 1"
                                />
                              </v-tab-item>
                              <v-tab-item key="time">
                                <v-time-picker
                                  v-if="dateTimeTab === 1 && form.payment_date"
                                  v-model="form.payment_time"
                                  no-title
                                  color="primary"
                                  format="24hr"
                                />
                              </v-tab-item>
                            </v-tabs-items>
                          </v-card-text>
                          <v-card-actions>
                            <v-spacer />
                            <v-btn text @click="datePickerMenu = false">Cancel</v-btn>
                            <v-btn
                              text
                              color="primary"
                              @click="datePickerMenu = false"
                              :disabled="!form.payment_date"
                            >
                              OK
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-menu>
                    </v-col>

                    <v-col cols="12" md="6">
                      <div class="d-flex align-center h-100">
                        <v-chip
                          :color="getStatusColor(form.payout_status)"
                          class="text-capitalize"
                        >
                          <v-icon left small>{{ getStatusIcon(form.payout_status) }}</v-icon>
                          {{ getStatusText(form.payout_status) }}
                        </v-chip>
                      </div>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.remarks"
                        label="Remarks"
                        placeholder="Enter remarks or notes about this payout"
                        solo
                        rounded
                        rows="3"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.remarks"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-note-text</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>
                </div>

                <!-- Status and Actions Section -->
                <div class="form-section mb-2" v-if="form.payout_status === 0 && !isSeller">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-check-circle</v-icon>
                    Actions
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="d-flex gap-2">
                        <v-btn
                          color="success"
                          small
                          @click="showMarkCompletedDialog"
                          :disabled="!isEditMode"
                          class="mr-2"
                        >
                          <v-icon left small>mdi-check</v-icon>
                          Mark Completed
                        </v-btn>
                        <v-btn
                          color="error"
                          small
                          @click="showMarkFailedDialog"
                          :disabled="!isEditMode"
                        >
                          <v-icon left small>mdi-close</v-icon>
                          Mark Failed
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </div>

                <!-- Audit Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-information</v-icon>
                    Audit Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="form.created_by?.name || 'System'"
                        label="Created By"
                        solo
                        rounded
                        readonly
                        disabled
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-account-circle</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="formatDate(form.created_at)"
                        label="Created At"
                        solo
                        rounded
                        readonly
                        disabled
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template #prepend-inner>
                          <v-icon color="grey">mdi-clock</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Form Actions -->
            <v-card-actions class="form-actions py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Form Status -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      :color="valid ? 'success' : 'warning'"
                      outlined
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>{{
                        valid ? "mdi-check-circle" : "mdi-alert-circle"
                      }}</v-icon>
                      {{ valid ? "Form Valid" : "Form Invalid" }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      {{ isEditMode ? (valid ? "Ready to save" : "Please fix validation errors") : "Read-only mode" }}
                    </span>
                  </div>
                </v-col>

                <!-- Right Side: Action Buttons -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn
                      v-if="isEditMode"
                      outlined
                      color="grey darken-1"
                      @click="resetForm"
                      class="mr-2"
                      :disabled="loading"
                    >
                      <v-icon left>mdi-refresh</v-icon>
                      Reset
                    </v-btn>
                    <v-btn
                      v-if="isEditMode"
                      color="primary"
                      @click="updatePayout"
                      :loading="loading"
                      :disabled="!valid"
                      class="submit-button"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Update Payout
                    </v-btn>
                    <v-btn
                      v-if="!form.is_deleted && !isSeller"
                      color="error"
                      outlined
                      @click="deletePayout"
                      :disabled="loading"
                      class="ml-2"
                    >
                      <v-icon left>mdi-delete</v-icon>
                      Delete
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Mark Completed Dialog -->
      <v-dialog v-model="markCompletedDialog" max-width="500px">
        <v-card rounded="xxl">
          <v-card-title class="text-h6">Mark Payout as Completed</v-card-title>
          <v-card-text>
            <v-form ref="completedForm" v-model="validCompleted">
              <v-text-field
                v-model="completedForm.transaction_id"
                label="Transaction ID"
                prepend-inner-icon="mdi-identifier"
                solo
                rounded
                dense
                placeholder="Enter transaction ID from payment gateway"
              ></v-text-field>
              <v-menu
                v-model="completedDatePickerMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedCompletedDateTime"
                    label="Payment Date & Time"
                    prepend-inner-icon="mdi-calendar-clock"
                    solo
                    rounded
                    dense
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    @click:clear="clearCompletedDate"
                  />
                </template>
                <v-card>
                  <v-card-text class="pa-0">
                    <v-tabs v-model="completedDateTimeTab" fixed-tabs>
                      <v-tab key="date">
                        <v-icon>mdi-calendar</v-icon>
                        Date
                      </v-tab>
                      <v-tab key="time" :disabled="!completedForm.payment_date">
                        <v-icon>mdi-clock</v-icon>
                        Time
                      </v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="completedDateTimeTab">
                      <v-tab-item key="date">
                        <v-date-picker
                          v-model="completedForm.payment_date"
                          no-title
                          scrollable
                          color="primary"
                          @input="completedDateTimeTab = 1"
                        />
                      </v-tab-item>
                      <v-tab-item key="time">
                        <v-time-picker
                          v-if="completedDateTimeTab === 1 && completedForm.payment_date"
                          v-model="completedForm.payment_time"
                          no-title
                          color="primary"
                          format="24hr"
                        />
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="completedDatePickerMenu = false">Cancel</v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="completedDatePickerMenu = false"
                      :disabled="!completedForm.payment_date"
                    >
                      OK
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="markCompletedDialog = false">Cancel</v-btn>
            <v-btn
              color="success"
              @click="markCompleted"
              :loading="processingCompleted"
            >
              Mark Completed
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Mark Failed Dialog -->
      <v-dialog v-model="markFailedDialog" max-width="500px">
        <v-card rounded="xxl">
          <v-card-title class="text-h6">Mark Payout as Failed</v-card-title>
          <v-card-text>
            <v-form ref="failedForm" v-model="validFailed">
              <v-textarea
                v-model="failedForm.reason"
                label="Failure Reason"
                prepend-inner-icon="mdi-alert"
                solo
                rounded
                dense
                rows="3"
                placeholder="Describe why the payout failed..."
              ></v-textarea>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="markFailedDialog = false">Cancel</v-btn>
            <v-btn
              color="error"
              @click="markFailed"
              :loading="processingFailed"
            >
              Mark Failed
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import { splitIstDateTime, combineIstDateTime, nowInIst, APP_TIMEZONE } from "@/utils/dateTime";

export default {
  name: "PayoutEdit",
  data() {
    return {
      valid: false,
      loading: false,
      loadingUsers: false,
      isEditMode: false,
      
      // Date/Time picker
      datePickerMenu: false,
      dateTimeTab: 0,
      
      // Completed form date/time picker
      completedDatePickerMenu: false,
      completedDateTimeTab: 0,
      
      // Form dialogs
      markCompletedDialog: false,
      markFailedDialog: false,
      processingCompleted: false,
      processingFailed: false,
      validCompleted: false,
      validFailed: false,
      
      completedForm: {
        transaction_id: "",
        payment_date: null,
        payment_time: null,
      },
      
      failedForm: {
        reason: "",
      },
      
      form: {
        user_id: null,
        amount: null,
        payment_method: null,
        payout_status: 0,
        transaction_id: "",
        generated_transaction_id: "",
        payment_date: null,
        payment_time: null,
        remarks: "",
        is_active: true,
        created_by: null,
        created_at: null,
        is_deleted: false,
      },

      originalForm: {},
      errors: {},

      // Dropdown options
      users: [],
      paymentMethods: [],
      payoutStatuses: [],

      amountRules: [
        (v) => !!v || "Amount is required",
        (v) => !isNaN(parseFloat(v)) || "Amount must be a valid number",
        (v) => parseFloat(v) > 0 || "Amount must be greater than 0",
        (v) => parseFloat(v) <= 999999.99 || "Amount cannot exceed 999,999.99",
      ],
    };
  },

  computed: {
    isSeller() {
      return this.user && this.user.position && this.user.position.name
        ? this.user.position.name.toUpperCase() === 'SELLER'
        : false;
    },

    canModifyAmount() {
      return this.form.payout_status === 0; // Only pending payouts can have amount modified
    },

    formattedPaymentDateTime() {
      if (!this.form.payment_date) return "";

      // payment_date is a plain YYYY-MM-DD string; parse/format in UTC so the
      // displayed day matches what was picked regardless of browser timezone.
      const date = new Date(`${this.form.payment_date}T00:00:00Z`);
      let formatted = date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });

      if (this.form.payment_time) {
        formatted += ` at ${this.form.payment_time}`;
      }

      return formatted;
    },

    combinedDateTime() {
      return combineIstDateTime(this.form.payment_date, this.form.payment_time);
    },

    formattedCompletedDateTime() {
      if (!this.completedForm.payment_date) return "";

      const date = new Date(`${this.completedForm.payment_date}T00:00:00Z`);
      let formatted = date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });

      if (this.completedForm.payment_time) {
        formatted += ` at ${this.completedForm.payment_time}`;
      }

      return formatted;
    },

    completedCombinedDateTime() {
      return combineIstDateTime(this.completedForm.payment_date, this.completedForm.payment_time);
    },
  },

  mounted() {
    this.getDropdownData();
    this.getPayout();
  },

  methods: {
    async getPayout() {
      this.loading = true;
      try {
        const response = await axios.get(`/payouts/${this.$route.params.id}`);
        this.form = { ...response.data.data };
        this.originalForm = { ...response.data.data };
        
        // Parse datetime for date/time pickers
        if (this.form.payment_date) {
          const { date, time } = splitIstDateTime(this.form.payment_date);
          this.form.payment_date = date;
          this.form.payment_time = time;
        } else {
          this.form.payment_time = null;
        }
      } catch (error) {
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.loading = false;
      }
    },

    async getDropdownData() {
      this.loadingUsers = true;
      try {
        const response = await axios.get("/masters", {
          params: { context: "payouts" }
        });
        
        this.users = response.data.users.map(user => ({
          text: `${user.name} (${user.email})`,
          value: user.id,
        }));
        
        this.paymentMethods = response.data.payment_methods;
        this.payoutStatuses = response.data.payout_statuses;
      } catch (error) {
        console.error("Failed to load dropdown data:", error);
      } finally {
        this.loadingUsers = false;
      }
    },

    async updatePayout() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      this.errors = {};

      try {
        // Prepare form data with combined date-time
        const formData = { ...this.form };
        if (this.combinedDateTime) {
          formData.payment_date = this.combinedDateTime;
        }
        
        // Remove the separate time field as it's combined with date
        delete formData.payment_time;

        const response = await axios.put(`/payouts/${this.$route.params.id}`, formData);
        
        this.$store.dispatch("errors/showSuccess", "Payout updated successfully!");
        this.form = { ...response.data.data };
        this.originalForm = { ...response.data.data };
        
        // Re-parse the updated datetime
        if (this.form.payment_date) {
          const { date, time } = splitIstDateTime(this.form.payment_date);
          this.form.payment_date = date;
          this.form.payment_time = time;
        }

        this.isEditMode = false;
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};
        }
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.loading = false;
      }
    },

    async deletePayout() {
      if (
        confirm(
          `Are you sure you want to delete payout ${this.form.generated_transaction_id}?`
        )
      ) {
        try {
          await axios.post(`/payouts/delete/${this.$route.params.id}`);
          this.$store.dispatch("errors/showSuccess", "Payout deleted successfully");
          this.$router.push("/payouts");
        } catch (error) {
          this.$store.dispatch("errors/handleError", error);
        }
      }
    },

    showMarkCompletedDialog() {
      const { date, time } = nowInIst();
      this.completedForm = {
        transaction_id: this.form.transaction_id || "",
        payment_date: date,
        payment_time: time,
      };
      this.completedDateTimeTab = 0;
      this.markCompletedDialog = true;
    },

    async markCompleted() {
      this.processingCompleted = true;
      try {
        const formData = { ...this.completedForm };
        if (this.completedCombinedDateTime) {
          formData.payment_date = this.completedCombinedDateTime;
        }
        delete formData.payment_time;

        await axios.post(`/payouts/${this.$route.params.id}/mark-completed`, formData);
        this.$store.dispatch("errors/showSuccess", "Payout marked as completed");
        this.markCompletedDialog = false;
        this.getPayout();
      } catch (error) {
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.processingCompleted = false;
      }
    },

    clearCompletedDate() {
      this.completedForm.payment_date = null;
      this.completedForm.payment_time = null;
      this.completedDateTimeTab = 0;
    },

    showMarkFailedDialog() {
      this.failedForm = { reason: "" };
      this.markFailedDialog = true;
    },

    async markFailed() {
      this.processingFailed = true;
      try {
        await axios.post(`/payouts/${this.$route.params.id}/mark-failed`, this.failedForm);
        this.$store.dispatch("errors/showSuccess", "Payout marked as failed");
        this.markFailedDialog = false;
        this.getPayout();
      } catch (error) {
        this.$store.dispatch("errors/handleError", error);
      } finally {
        this.processingFailed = false;
      }
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      if (!this.isEditMode) {
        this.resetForm();
      }
    },

    resetForm() {
      this.form = { ...this.originalForm };
      this.errors = {};
      this.dateTimeTab = 0;
      
      // Re-parse the original datetime
      if (this.originalForm.payment_date) {
        const { date, time } = splitIstDateTime(this.originalForm.payment_date);
        this.form.payment_date = date;
        this.form.payment_time = time;
      } else {
        this.form.payment_time = null;
      }
      
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    clearPaymentDate() {
      this.form.payment_date = null;
      this.form.payment_time = null;
      this.dateTimeTab = 0;
    },

    getInitials(name) {
      if (!name) return "U";
      return name
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    getStatusColor(status) {
      const colors = {
        0: "warning", // Pending
        1: "success", // Completed
        2: "error",   // Failed
      };
      return colors[status] || "grey";
    },

    getStatusText(status) {
      const texts = {
        0: "Pending",
        1: "Completed",
        2: "Failed",
      };
      return texts[status] || "Unknown";
    },

    getStatusIcon(status) {
      const icons = {
        0: "mdi-clock-outline",
        1: "mdi-check-circle",
        2: "mdi-close-circle",
      };
      return icons[status] || "mdi-help-circle";
    },

    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: APP_TIMEZONE,
      });
    },
  },
};
</script>

<style scoped>
.page-header-card {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-bottom: 1px solid #e0e0e0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 0.875rem;
  opacity: 0.8;
}

.back-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

.form-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-bottom: 1px solid #e0e0e0;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.form-content {
  background-color: #fafafa;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.professional-field >>> .v-input__control {
  border-radius: 12px !important;
}

.professional-field >>> .v-input__slot {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06) !important;
}

.form-actions {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-top: 1px solid #e0e0e0;
}

.submit-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  padding: 8px 24px;
}

.v-chip {
  font-weight: 500;
}

.text-caption {
  line-height: 1.4;
}
</style>